import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData, useSearchParams } from 'react-router-dom'
import Smartlook from 'smartlook-client'
import styled from 'styled-components'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { Input } from '@/components/Input.tsx'
import { InputCaption, Label } from '@/components/Label.tsx'
import { NotificationError } from '@/components/Notification.tsx'
import {
  FormElement,
  FormElementError,
  FormStyled,
  FormSubmit,
} from '@/components/templates/Form.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Header } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { useAppointmentStore } from '@/store.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

import { ActionData } from './actions.ts'

const projectId = import.meta.env.XPERT_SMARTLOOK_KEY
Smartlook.init(projectId, { region: 'eu' })

const ButtonWrapper = styled(FormSubmit)`
  position: absolute;
  bottom: 32px;
  width: calc(100% - 40px);
`
const BookWithReferral = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'bookWithReferral',
  })
  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.ReferralCodeTrue,
  )

  const [params] = useSearchParams()
  const { setReferralCode, zdCode } = useAppointmentStore((state) => ({
    setReferralCode: state.setReferralCode,
    zdCode: state.zdCode || params.get('ZD') || '',
  }))

  const actionData = useActionData() as ActionData | undefined

  useEffect(() => {
    const error = actionData?.errors
      ? actionData.errors.map((e) => e.message).join(',')
      : actionData?.error
    if (error) {
      trackFormError({
        error,
      })
    }
  }, [actionData?.errors, actionData?.error])

  return (
    <ScreenWrapper>
      <FormStyled method="post" align="center">
        <Header>{t('header')}</Header>
        <FormElement>
          <Label htmlFor="referralCode">{t('referralCode.label')}</Label>
          <Input
            type="text"
            name="referralCode"
            value={zdCode}
            placeholder={t('referralCode.placeholder')}
            onBlur={(e) => {
              const value = e.target.value
              if (value && value.match(/^\d*$/)) {
                setReferralCode(`ZD${value}`)
              }
            }}
            onFocus={() => {
              trackFormInteraction({
                action: 'click',
                formFieldName: 'referralCode',
              })
            }}
            onChange={(e) => setReferralCode(e.target.value)}
          />
          {actionData?.errors?.find((e) => e.path[0] === 'referralCode') && (
            <FormElementError>
              {t(`errorMessage.referralCode`)}
            </FormElementError>
          )}
        </FormElement>

        <FormElement>
          <Label htmlFor="dateOfBirth">{t('dateOfBirth.label')}</Label>
          <Input
            type="date"
            name="dateOfBirth"
            placeholder={t('dateOfBirth.placeholder')}
            defaultValue={params.get('DOB') ?? undefined}
            onBlur={() =>
              trackFormInteraction({
                action: 'click',
                formFieldName: 'dateOfBirth',
              })
            }
          />
          {actionData?.errors?.find((e) => e.path[0] === 'dateOfBirth') && (
            <FormElementError>{t(`errorMessage.dateOfBirth`)}</FormElementError>
          )}
        </FormElement>
        <InputCaption>{t('caption')}</InputCaption>
        {actionData?.error && (
          <NotificationError label={t(`errorMessage.${actionData.error}`)} />
        )}

        <ButtonWrapper>
          <StyledActionButton
            variant="Primary"
            type="submit"
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('actions.checkReferralCode')}
          </StyledActionButton>
        </ButtonWrapper>
      </FormStyled>
    </ScreenWrapper>
  )
}

export { BookWithReferral }
